import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { useSession } from '../lib/auth-client.js';
import { supportToken } from '../lib/fleet-token.js';
import { useAction, useAsync } from '../lib/useAsync.js';
import { usePersonName } from '../lib/people.js';
import { Conversation } from './SupportThread.js';
import {
  PRIORITY_LABELS,
  SAVED_VIEWS,
  STATUS_LABELS,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  VIEW_LABELS,
  categoryOptions,
  categoryText,
  describeEvent,
  statusTone,
  supportApi,
  type CategoryLabel,
  type Inbox,
  type QueueTicket,
  type Requester,
  type SavedView,
  type TicketDetail,
  type TicketPriority,
  type TicketStatus,
} from '../lib/support-api.js';
import { FilterPills, Screen } from '../components/Screen.js';
import { RecordScreen, TabNav, type RecordTabs } from '../components/Record.js';
import { FeedbackDetailScreen, FeedbackReview, SupportOverviewScreen } from './SupportReview.js';
import { Select } from '../components/Select.js';
import { NewTicket } from '../modals/NewTicket.js';
import { Redirect, movedSupportPath } from './SupportConfiguration.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Note,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
  inputStyle,
} from '../components/ui.js';

/**
 * The support queue and one ticket (RidgeWill/platform#29).
 *
 * The data is the support service's, not auth's: every call goes to
 * `support-api-<env>` with a bearer token for support's audience
 * (lib/support-api.ts, `supportToken`), so the first visit on a page makes one
 * silent sign-in round trip, as CI fleet does.
 *
 * `#/support` is the queue, `#/support/<reference>` one ticket.
 */
const api = supportApi(supportToken);

export const supportPath = (reference?: string) =>
  reference ? `/support/${encodeURIComponent(reference)}` : '/support';

/**
 * Three sections as path segments, like Subscriptions (org-admin#140):
 * `#/support/tickets` (also bare `#/support`), `#/support/feedback`,
 * `#/support/overview`. Anything else is a ticket reference. The two cannot
 * collide: a reference is capitals and a hyphen. Categories, form mapping,
 * settings and suspended IPs live in Support > Configuration.
 */
type Section = 'tickets' | 'feedback' | 'overview';
// Overview first (Jon, 2026-10-04). Bare `#/support` still opens Tickets, the
// queue being where support work starts.
const TABS: RecordTabs<Section> = [
  { path: 'overview', label: 'Overview' },
  { path: 'tickets', label: 'Tickets' },
  { path: 'feedback', label: 'Feedback' },
];

/**
 * Sections that moved to Support > Configuration (Jon, 2026-10-11). A link to the
 * old place is sent on rather than read as a ticket reference called "categories".
 */
const MOVED_TO_SETTINGS = new Set(['categories', 'mapping', 'suspended-ips']);

export function Support({
  reference,
  detail,
  navigate,
}: {
  reference?: string;
  /** `#/support/feedback/<id>`: one piece of feedback; `#/support/suspended-ips/<network>`: one network. */
  detail?: string;
  navigate: (to: string) => void;
}) {
  const section = TABS.find((t) => t.path === (reference ?? 'tickets'))?.path;
  if (reference && MOVED_TO_SETTINGS.has(reference)) {
    return <Redirect to={movedSupportPath(reference, detail)} navigate={navigate} />;
  }
  if (reference && !section) return <Ticket key={reference} reference={reference} />;
  const tabs = <TabNav base="/support" tabs={TABS} current={section} label="Support sections" />;
  if (section === 'feedback' && detail) {
    return <FeedbackDetailScreen key={detail} api={api} id={detail} tabs={tabs} navigate={navigate} />;
  }
  if (section === 'feedback') return <FeedbackReview api={api} tabs={tabs} navigate={navigate} />;
  if (section === 'overview') return <SupportOverviewScreen api={api} tabs={tabs} />;
  return <Queue navigate={navigate} tabs={tabs} />;
}

// ── The queue ───────────────────────────────────────────────────────────

function Queue({ navigate, tabs }: { navigate: (to: string) => void; tabs: ReactNode }) {
  const { data: session } = useSession();
  const me = session?.user.id;
  const [view, setView] = useState<SavedView>('all-open');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState<string>();
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const [creating, setCreating] = useState(false);
  /** A category id; the queue shows it and everything under it (support#68). */
  const [category, setCategory] = useState('');

  const counts = useAsync(() => api.counts(), []);
  const tree = useAsync(() => api.categories(), []);
  const { state, reload } = useAsync(
    () => api.queue({ view, q: search, cursor, ...(category ? { category } : {}) }),
    [view, search, cursor, category],
  );

  const count = (v: SavedView) => (counts.state.status === 'ready' ? counts.state.data.counts[v] : undefined);

  return (
    <Screen
      filters={tabs}
      chips={
        <FilterPills
          label="Saved view"
          current={view}
          onSelect={(v) => {
            setView(v as SavedView);
            setCursors([]);
          }}
          options={SAVED_VIEWS.map((v) => ({
            value: v,
            label: count(v) === undefined ? VIEW_LABELS[v] : `${VIEW_LABELS[v]} (${count(v)})`,
          }))}
        />
      }
      actions={
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(q.trim() || undefined);
              setCursors([]);
            }}
          >
            <input
              aria-label="Search subjects"
              placeholder="Search subjects, then press Enter…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ ...inputStyle, width: '16rem' }}
            />
          </form>
          <Select
            label="Category"
            value={category}
            onChange={(v) => {
              setCategory(v);
              setCursors([]);
            }}
            options={[
              { value: '', label: 'All categories' },
              ...(tree.state.status === 'ready' ? categoryOptions(tree.state.data.categories) : []),
            ]}
          />
          <Button
            onClick={() => {
              reload();
              counts.reload();
            }}
          >
            Refresh
          </Button>
          <Button onClick={() => setCreating(true)}>New ticket</Button>
        </>
      }
    >
      {creating ? (
        <NewTicket
          api={api}
          me={session ? { id: session.user.id, email: session.user.email ?? null } : undefined}
          onClose={() => setCreating(false)}
          onCreated={(reference) => navigate(supportPath(reference))}
        />
      ) : null}
      <Panel title={VIEW_LABELS[view]}>
        <AsyncBoundary state={state}>
          {(d) => (
            <>
              {d.tickets.length === 0 ? (
                <EmptyState
                  title={search ? 'No ticket subject matches that search.' : 'Nothing here.'}
                  suggestion={view === 'all-open' ? 'The queue is clear.' : 'Try another view.'}
                />
              ) : (
                <Table
                  columns={['Ticket', 'Category', 'Requester', 'Product', 'Status', 'Priority', 'Assignee', 'Last from them']}
                >
                  {d.tickets.map((t) => (
                    <tr
                      key={t.reference}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(supportPath(t.reference))}
                    >
                      <Td>
                        <a href={`#${supportPath(t.reference)}`} style={s.ref} onClick={(e) => e.stopPropagation()}>
                          {t.reference}
                        </a>
                        <div style={s.subject}>{t.subject}</div>
                      </Td>
                      <Td>
                        <CategoryCell category={t.category} />
                      </Td>
                      <Td>
                        <RequesterText requester={t.requester} />
                      </Td>
                      <Td>{t.product}</Td>
                      <Td>
                        <TicketBadges ticket={t} />
                      </Td>
                      <Td>
                        <PriorityBadge priority={t.priority} />
                      </Td>
                      <Td>
                        <Assignee userId={t.assigneeUserId} me={me} />
                      </Td>
                      <Td>
                        <When iso={t.lastInboundAt ?? t.openedAt} />
                      </Td>
                    </tr>
                  ))}
                </Table>
              )}
              <Pager
                page={cursors.length + 1}
                hasNext={d.hasMore && Boolean(d.nextCursor)}
                onPrevious={() => setCursors(cursors.slice(0, -1))}
                onNext={() => d.nextCursor && setCursors([...cursors, d.nextCursor])}
              />
            </>
          )}
        </AsyncBoundary>
      </Panel>
    </Screen>
  );
}

// ── One ticket ─────────────────────────────────────────────────────────

function Ticket({ reference }: { reference: string }) {
  const { state, reload } = useAsync(() => api.ticket(reference), [reference]);

  if (state.status === 'loading') {
    return (
      <Screen>
        <Note>Loading…</Note>
      </Screen>
    );
  }
  if (state.status === 'failed') {
    return (
      <Screen>
        <a href={`#${supportPath()}`} style={s.back}>
          &larr; Support
        </a>
        <Note tone="danger">{state.message}</Note>
      </Screen>
    );
  }
  return <TicketRecord ticket={state.data} reload={() => reload(true)} />;
}

function TicketRecord({ ticket: t, reload }: { ticket: TicketDetail; reload: () => void }) {
  const { data: session } = useSession();
  const me = session?.user.id;
  const action = useAction();
  const tree = useAsync(() => api.categories(), []);
  const act = (fn: () => Promise<string>) =>
    void action.run(fn).then((ok) => {
      if (ok) reload();
    });

  return (
    <RecordScreen
      back={{ to: supportPath(), label: 'Support' }}
      title={t.subject}
      identifier={t.reference}
      badges={
        <>
          <TicketBadges ticket={t} />
          <PriorityBadge priority={t.priority} />
          <InboxBadge inbox={t.inbox} />
        </>
      }
      description={
        <>
          {t.product} · {channelLabel(t.channel)} · opened <When iso={t.openedAt} />
        </>
      }
    >
      <OutcomeNote outcome={action.outcome} />

      <Panel title="Ticket">
        <PanelBody>
          <div style={s.facts}>
            <Fact label="Requester">
              <RequesterText requester={t.requester} link />
            </Fact>
            <Fact label="Assignee">
              <Assignee userId={t.assigneeUserId} me={me} />
            </Fact>
            <Fact label="First response">
              {t.firstResponseAt ? <When iso={t.firstResponseAt} /> : <span style={s.muted}>Not yet</span>}
            </Fact>
            <Fact label="Resolved">
              {t.resolvedAt ? <When iso={t.resolvedAt} /> : <span style={s.muted}>No</span>}
            </Fact>
            {t.tags.length ? <Fact label="Tags">{t.tags.join(', ')}</Fact> : null}
          </div>
          {t.requester.closedAt ? (
            <Note tone="warn">
              The requester closed their account <When iso={t.requester.closedAt} />. Their details were removed
              and no reply will be emailed. The ticket stays open until somebody here decides it is done.
            </Note>
          ) : null}
          {!t.category && t.status !== 'RESOLVED' && t.flag === 'NONE' ? (
            <Note tone="warn">Choose a category before resolving this ticket.</Note>
          ) : null}
          <div style={s.controls}>
            <Labelled label="Status">
              <Select
                label="Status"
                value={t.status}
                disabled={action.running}
                options={[
                  // Shown while a draft waits, never offered: only saving a draft sets it (support#79).
                  ...(t.status === 'WAITING_FOR_REVIEW'
                    ? [{ value: t.status, label: STATUS_LABELS.WAITING_FOR_REVIEW }]
                    : []),
                  ...TICKET_STATUSES.map((v) => ({ value: v, label: STATUS_LABELS[v] })),
                ]}
                onChange={(status) =>
                  act(async () => {
                    await api.setStatus(t.reference, status);
                    return `Status set to ${STATUS_LABELS[status]}.`;
                  })
                }
              />
            </Labelled>
            <Labelled label="Priority">
              <Select
                label="Priority"
                value={t.priority}
                disabled={action.running}
                options={TICKET_PRIORITIES.map((v) => ({ value: v, label: PRIORITY_LABELS[v] }))}
                onChange={(priority: TicketPriority) =>
                  act(async () => {
                    await api.setPriority(t.reference, priority);
                    return `Priority set to ${PRIORITY_LABELS[priority]}.`;
                  })
                }
              />
            </Labelled>
            <Labelled label="Category">
              <Select
                label="Category"
                value={t.category?.id ?? ''}
                disabled={action.running || tree.state.status !== 'ready'}
                options={[
                  { value: '', label: t.category ? 'Clear the category' : 'Needs a category' },
                  // The current one stays listed even if it was since archived.
                  ...(t.category && tree.state.status === 'ready' && !categoryOptions(tree.state.data.categories, t.product).some((o) => o.value === t.category!.id)
                    ? [{ value: t.category.id, label: categoryText(t.category) }]
                    : []),
                  ...(tree.state.status === 'ready' ? categoryOptions(tree.state.data.categories, t.product) : []),
                ]}
                onChange={(id) =>
                  act(async () => {
                    const res = await api.setCategory(t.reference, id || null);
                    return res.category ? `Filed under ${categoryText(res.category)}.` : 'Category cleared.';
                  })
                }
              />
            </Labelled>
            <div style={s.buttons}>
              {me && t.assigneeUserId !== me ? (
                <Button
                  disabled={action.running}
                  onClick={() =>
                    act(async () => {
                      await api.assign(t.reference, me);
                      return 'Assigned to you.';
                    })
                  }
                >
                  Assign to me
                </Button>
              ) : null}
              {t.assigneeUserId ? (
                <Button
                  disabled={action.running}
                  onClick={() =>
                    act(async () => {
                      await api.assign(t.reference, null);
                      return 'Unassigned.';
                    })
                  }
                >
                  Unassign
                </Button>
              ) : null}
              <Button
                tone={t.flag === 'SUSPECTED_SPAM' ? undefined : 'danger'}
                disabled={action.running}
                onClick={() => {
                  const spam = t.flag !== 'SUSPECTED_SPAM';
                  act(async () => {
                    await api.setSpam(t.reference, spam);
                    return spam ? 'Marked as spam.' : 'Spam mark removed.';
                  });
                }}
              >
                {t.flag === 'SUSPECTED_SPAM' ? 'Not spam' : 'Mark as spam'}
              </Button>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/*
        The composer, holding any draft waiting for review, above the thread:
        the thread reads newest first, so what you are about to send sits on
        top of what it answers (Jon, 2026-10-17).
      */}
      <Reply ticket={t} me={me} onSent={reload} />

      <Panel title="Conversation">
        <PanelBody>
          <Conversation
            reference={t.reference}
            messages={t.messages}
            openedAt={t.openedAt}
            me={me}
            deletes={{ events: t.events, allowed: t.canDeleteAttachments === true, onDeleted: reload }}
          />
        </PanelBody>
      </Panel>

      <Panel title="History">
        <Table columns={['When', 'What', 'By']}>
          {t.events.map((e) => (
            <tr key={e.id}>
              <Td>
                <When iso={e.at} />
              </Td>
              <Td>{describeEvent(e)}</Td>
              <Td>
                {e.actorUserId ? (
                  <>
                    <Assignee userId={e.actorUserId} me={me} />
                    {e.actorIsAgent ? (
                      <>
                        {' '}
                        <Badge>agent</Badge>
                      </>
                    ) : null}
                  </>
                ) : (
                  <span style={s.muted}>The service</span>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>

      {t.device ? (
        <Panel title="Device">
          <Table columns={['Field', 'Value']}>
            {Object.entries(t.device)
              .filter(([k, v]) => v !== null && v !== '' && !['id', 'ticketId', 'createdAt'].includes(k))
              .map(([k, v]) => (
                <tr key={k}>
                  <Td mono>{k}</Td>
                  <Td>{String(v)}</Td>
                </tr>
              ))}
          </Table>
        </Panel>
      ) : null}
    </RecordScreen>
  );
}

/**
 * The composer, with any draft waiting for review already in it (support#79;
 * Jon, 2026-10-17: "it needs to reside in the same window as the Reply").
 *
 * A pending draft, usually the support agent's, fills the reply box with
 * "Drafted by" and Clear above it on the right. Send reply sends it, edited
 * or not, as the person pressing it, without the AI notice; that needs a
 * support admin, which support decides (`canSend`). Clear discards the draft
 * and empties the box for somebody who would rather write their own. Save for
 * review replaces the draft with what is in the box. The note box keeps its
 * own text, so switching to a note and back does not lose the draft.
 */
function Reply({ ticket: t, me, onSent }: { ticket: TicketDetail; me?: string; onSent: () => void }) {
  /** Made from feedback whose sender asked not to be contacted: notes only (support#45). */
  const notesOnly = t.contactAllowed === false;
  const draft = notesOnly ? null : (t.draft ?? null);
  const [reply, setReply] = useState(draft?.body ?? '');
  const [note, setNote] = useState('');
  const [internal, setInternal] = useState(notesOnly);
  const [statusAfter, setStatusAfter] = useState<TicketStatus | ''>('');
  const action = useAction();
  const noEmail = !internal && Boolean(t.requester.closedAt);

  // A newer draft, or none any more, replaces what the box holds.
  useEffect(() => setReply(draft?.body ?? ''), [draft?.id, draft?.savedAt, draft?.body]);

  const body = internal ? note : reply;
  const setBody = internal ? setNote : setReply;
  const fromDraft = !internal && draft !== null;
  const edited = fromDraft && reply.trim() !== draft.body.trim();
  const cannotSend = fromDraft && !draft.canSend;

  const after = (ok: boolean) => {
    if (!ok) return;
    if (internal) setNote('');
    else setReply('');
    setStatusAfter('');
    onSent();
  };

  return (
    <Panel title={notesOnly ? 'Internal note' : 'Reply'}>
      <PanelBody>
        {notesOnly ? (
          <Note tone="warn">
            The sender asked not to be contacted, so this ticket has no requester and takes internal notes only. Nothing
            here is emailed.
          </Note>
        ) : null}
        <div style={s.replyHead}>
          <div style={{ ...s.replyModes, ...(notesOnly ? { display: 'none' } : {}) }} role="radiogroup" aria-label="Kind of message">
            <label>
              <input type="radio" checked={!internal} onChange={() => setInternal(false)} /> Reply to the requester
            </label>
            <label>
              <input type="radio" checked={internal} onChange={() => setInternal(true)} /> Internal note (staff only)
            </label>
          </div>
          {fromDraft ? (
            <div style={s.draftBy}>
              <span style={s.muted}>Drafted by</span>
              <Assignee userId={draft.authorUserId} me={me} />
              {draft.authorIsAgent ? <Badge>agent</Badge> : null}
              <span style={s.muted}>
                <When iso={draft.savedAt} />
              </span>
              <Button
                disabled={action.running}
                onClick={() =>
                  void action
                    .run(async () => {
                      await api.discardDraft(t.reference, draft.id);
                      return 'Draft cleared. Nothing was emailed.';
                    })
                    .then(after)
                }
              >
                Clear
              </Button>
            </div>
          ) : null}
        </div>
        <textarea
          aria-label={internal ? 'Internal note' : 'Reply'}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={fromDraft ? 9 : 6}
          style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
          placeholder={internal ? 'Only staff will see this.' : 'This is emailed to the requester.'}
        />
        {noEmail ? (
          <Note tone="warn">The requester closed their account, so this is saved on the ticket and not emailed.</Note>
        ) : null}
        {cannotSend ? (
          <Note>Only a support admin can send a drafted reply. Clear it to write your own.</Note>
        ) : null}
        <div style={s.replyFoot}>
          <Labelled label="Then set status">
            <Select<TicketStatus | ''>
              label="Then set status"
              value={statusAfter}
              onChange={setStatusAfter}
              options={[
                { value: '', label: internal ? 'Leave as it is' : 'Automatic' },
                ...TICKET_STATUSES.map((v) => ({ value: v, label: STATUS_LABELS[v] })),
              ]}
            />
          </Labelled>
          <div style={s.buttons}>
            {!internal ? (
              <Button
                disabled={action.running || !body.trim() || (fromDraft && !edited)}
                onClick={() =>
                  void action
                    .run(async () => {
                      await api.saveDraft(t.reference, body.trim());
                      return 'Saved for review. Nothing was emailed.';
                    })
                    .then(after)
                }
              >
                Save for review
              </Button>
            ) : null}
            <Button
              disabled={action.running || !body.trim() || cannotSend}
              onClick={() =>
                void action
                  .run(async () => {
                    if (fromDraft) {
                      await api.sendDraft(t.reference, draft.id, {
                        ...(edited ? { body: body.trim() } : {}),
                        ...(statusAfter ? { statusAfter } : {}),
                      });
                    } else {
                      await api.reply(t.reference, {
                        body: body.trim(),
                        internal,
                        ...(statusAfter ? { statusAfter } : {}),
                      });
                    }
                    return internal ? 'Note added.' : noEmail ? 'Reply saved.' : 'Reply sent.';
                  })
                  .then(after)
              }
            >
              {internal ? 'Add note' : 'Send reply'}
            </Button>
          </div>
        </div>
        <OutcomeNote outcome={action.outcome} />
      </PanelBody>
    </Panel>
  );
}

// ── Small pieces ───────────────────────────────────────────────────────

function TicketBadges({ ticket }: { ticket: Pick<QueueTicket, 'status' | 'flag' | 'requester' | 'contactAllowed'> }) {
  return (
    <span style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap' }}>
      <Badge tone={statusTone(ticket.status)}>{STATUS_LABELS[ticket.status]}</Badge>
      {ticket.contactAllowed === false ? <Badge tone="warn">Do not contact</Badge> : null}
      {ticket.flag === 'SUSPECTED_SPAM' ? <Badge tone="danger">Spam</Badge> : null}
      {ticket.requester.closedAt ? <Badge>Account closed</Badge> : null}
    </span>
  );
}

/** privacy@ and legal@ tickets say so (support#67); support's own say nothing. */
function InboxBadge({ inbox }: { inbox?: Inbox }) {
  if (inbox === 'PRIVACY') return <Badge>privacy@</Badge>;
  if (inbox === 'LEGAL') return <Badge>legal@</Badge>;
  return null;
}

/** The category in the queue: its name, with the path above it when it is nested. */
function CategoryCell({ category }: { category?: CategoryLabel | null }) {
  if (category === undefined) return null;
  if (!category) return <span style={{ color: C.warnText, fontWeight: 600, fontSize: '.85rem' }}>Needs category</span>;
  const parents = category.trail.slice(0, -1);
  return (
    <div title={categoryText(category)}>
      <div style={{ fontSize: '.88rem', fontWeight: 600 }}>{category.name}</div>
      {parents.length ? <div style={s.subject}>{parents.join(' › ')}</div> : null}
    </div>
  );
}

function PriorityBadge({ priority }: { priority: TicketPriority }) {
  const tone = priority === 'URGENT' ? 'danger' : priority === 'HIGH' ? 'warn' : 'neutral';
  return <Badge tone={tone}>{PRIORITY_LABELS[priority]}</Badge>;
}

/**
 * Who asked. The address when there is one; a closed requester has none, and
 * says so rather than showing a blank.
 */
export function requesterLabel(r: Requester): string {
  if (r.closedAt) return 'Closed account';
  if (!r.userId && !r.email) return 'Nobody (asked not to be contacted)';
  return r.email ?? 'No address';
}

function RequesterText({ requester: r, link }: { requester: Requester; link?: boolean }) {
  const label = requesterLabel(r);
  const style = r.closedAt || !r.email ? s.mutedItalic : undefined;
  if (link && r.userId && !r.closedAt) {
    return (
      <a href={`#/users/${encodeURIComponent(r.userId)}`} style={{ color: C.text }}>
        {label}
      </a>
    );
  }
  return <span style={style}>{label}</span>;
}

function Assignee({ userId, me }: { userId: string | null; me?: string }) {
  const name = usePersonName(userId && userId !== me ? userId : null);
  if (!userId) return <span style={s.muted}>Nobody</span>;
  if (me && userId === me) return <span>You</span>;
  return (
    <a
      href={`#/users/${encodeURIComponent(userId)}`}
      style={{ color: C.text, ...(name ? {} : { fontFamily: 'monospace' }) }}
      title={userId}
    >
      {name ?? userId.slice(0, 8)}
    </a>
  );
}

/** A label over a control that is not a native input, so `<label>` cannot wrap it. */
function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}>
      <span style={{ fontWeight: 600, color: C.textMid }}>{label}</span>
      {children}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div style={s.factLabel}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

export function Pager({
  page,
  hasNext,
  onPrevious,
  onNext,
}: {
  page: number;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  if (page === 1 && !hasNext) return null;
  return (
    <div style={s.pager}>
      <span style={{ fontSize: '.82rem', color: C.muted }}>Page {page}</span>
      <span style={{ display: 'flex', gap: '.4rem', marginLeft: 'auto' }}>
        <Button disabled={page === 1} onClick={onPrevious}>
          Previous
        </Button>
        <Button disabled={!hasNext} onClick={onNext}>
          Next
        </Button>
      </span>
    </div>
  );
}

const CHANNELS: Record<string, string> = {
  WEB_FORM: 'web form',
  ACCOUNT_PORTAL: 'account portal',
  IN_APP: 'in app',
  EMAIL: 'email',
  STAFF: 'opened by staff',
};

export const channelLabel = (channel: string) => CHANNELS[channel] ?? channel.toLowerCase();

/** How a message is labelled and tinted, by who it is from and who sees it. */
const s: Record<string, CSSProperties> = {
  back: { color: C.brand, textDecoration: 'none', fontSize: '.88rem' },
  ref: { fontFamily: 'monospace', color: C.brand, textDecoration: 'none', fontWeight: 600 },
  subject: { fontSize: '.85rem', color: C.textMid, marginTop: '.15rem' },
  muted: { color: C.muted },
  mutedItalic: { color: C.muted, fontStyle: 'italic' },
  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))', gap: '.9rem 1.2rem', marginBottom: '.9rem' },
  factLabel: { fontSize: '.75rem', fontWeight: 600, color: C.muted, marginBottom: '.2rem' },
  controls: { display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginTop: '.9rem' },
  buttons: { display: 'flex', gap: '.4rem', flexWrap: 'wrap' },
  messageHead: { display: 'flex', gap: '.5rem', alignItems: 'center', fontSize: '.84rem', marginBottom: '.4rem', flexWrap: 'wrap' },
  replyModes: { display: 'flex', gap: '1.2rem', fontSize: '.88rem' },
  replyHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.6rem 1rem', flexWrap: 'wrap', marginBottom: '.6rem', minHeight: '2rem' },
  draftBy: { display: 'flex', alignItems: 'center', gap: '.45rem', fontSize: '.84rem', marginLeft: 'auto', flexWrap: 'wrap' },
  replyFoot: { display: 'flex', gap: '1rem', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: '.7rem', flexWrap: 'wrap' },
  pager: { display: 'flex', alignItems: 'center', padding: '.7rem 1.2rem', borderTop: `1px solid ${C.border}` },
};
